import type { FC } from 'react'
import { memo, useState } from 'react'
import LoadingPage from 'src/components/LoadingPage'
import { ArtworkWarnedOverlay } from 'src/components/Media/MediaContentRemoved'
import { AppealButtonInDetail } from 'src/components/Report/AppealDialog'
import type {
  Artwork,
  Manga,
  MangaChapter,
} from 'src/services/apollo/generated/graphql'
import { RefType } from 'src/services/apollo/generated/graphql'
import { useGetMedia } from 'src/services/apollo/media'
import { isArtworkAppealable } from 'src/utils/artwork'
import { isUserContentSensitive } from 'src/utils/contentSafety'
import { cx } from 'src/utils/cx'
import { getPublicUrl } from 'src/utils/media'
import MangaLikeButton from '../Common/MangaLikeButton'

export interface MangaViewerProps {
  manga?: Manga
  chapter: MangaChapter
  artwork?: Artwork
}

const _MangaBlock: FC<{ mediaId: string; onLoad?: () => void }> = ({
  mediaId,
  onLoad,
}) => {
  const { data: media } = useGetMedia(mediaId)
  const url = getPublicUrl(media)

  return (
    <img
      className="w-full select-none pointer-events-none bg-black"
      src={url}
      crossOrigin="anonymous"
      loading="lazy"
      decoding="async"
      onLoad={onLoad}
    />
  )
}

const MangaBlock = memo(_MangaBlock)

const _MangaViewer: FC<MangaViewerProps> = ({ manga, chapter, artwork }) => {
  const { content } = chapter

  const [loading, setLoading] = useState(true)

  const mediaIds: string[] = Array.isArray(content?.mediaIds)
    ? content.mediaIds
    : []

  return (
    <div className="text-white">
      <div className="w-full text-primary bg-background min-h-[80px] flex flex-col justify-center items-center text-2xl font-bold p-4">
        <span className="whitespace-normal w-full break-word mb-2 text-center">
          {chapter.title}
        </span>

        {manga && <MangaLikeButton manga={manga} />}
      </div>

      <div className="mb-8 h-[1px] w-full bg-gray-300" />

      <div
        className={cx('flex flex-col w-full relative', {
          'min-h-[--visible-height]': loading,
        })}
      >
        {loading && (
          <div className="absolute w-full">
            <LoadingPage />
          </div>
        )}
        {mediaIds.map(mediaId => {
          return (
            <MangaBlock
              key={mediaId}
              mediaId={mediaId}
              onLoad={() => setLoading(false)}
            />
          )
        })}
        {manga?.artworkId && (
          <MangaArtworkOverlay artworkId={manga?.artworkId} artwork={artwork} />
        )}
      </div>
    </div>
  )
}

function MangaArtworkOverlay(props: { artworkId: string; artwork?: Artwork }) {
  /**
   * isWarned should be replaced with isSensitive in the future
   */
  const isWarned = isUserContentSensitive(props.artwork)
  const isAppealable = isArtworkAppealable(props.artwork)

  return isWarned ? (
    <div
      className={cx(
        'absolute w-full h-full bg-black/30 backdrop-blur-sm flex flex-col justify-center items-center',
      )}
    >
      <ArtworkWarnedOverlay className="px-4" placement="in-detail" />
      {isAppealable && (
        <AppealButtonInDetail
          className="mt-4"
          refType={RefType.Artwork}
          refId={props.artworkId}
        />
      )}
    </div>
  ) : null
}

const MangaViewer = memo(_MangaViewer)

export default MangaViewer
